<!-- src/views/CreateRecipe.vue -->
<template>
  <div class="main bg-primary">
    <TopDeck
      v-if="true"
      :firstElement="pageTitle"
      secondElement="une recette"
      :coloredSecond="true"
      actionHeaderIcon="mdi-arrow-left"
      :actionheaderFunction="() => $router.push('/my_recipes')"
      actionHeaderColor="accent"
      actionHeaderTextColor="white"
    />

    <v-form ref="form" v-model="isValid" lazy-validation class="mt-5">
      <!-- Métadonnées recette -->
      <v-card outlined class="mb-6 rounded-xl pa-2 px-2" color="secondary">
        <v-card-title class="text-h6">Informations recette</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="recipe.title" label="Nom de la recette" variant="outlined" required :hide-details="true"/>
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model.number="recipe.servings" type="number" min="1" label="Portions" variant="outlined" :hide-details="true" />
            </v-col>
            <v-col cols="6" md="3">
              <v-combobox
                v-model="recipe.category"
                :items="recipeCategories"
                item-title="text"
                item-value="value"
                label="Catégorie"
                clearable
                variant="outlined"
                :return-object="false"
                :hide-details="true"
              />
            </v-col>
            <v-col cols="12">
              <label class="text-body-2 ml-3 mb-1 d-block">Notes (optionnel)</label>
              <RichTextEditor v-model="recipe.notes" placeholder="Ajoutez vos notes ici (listes, mise en forme...)" />
            </v-col>
            <v-col cols="12">
              <v-combobox
                v-model="recipe.labels"
                label="Tags (ex: anti-inflammatoire, protéiné...)"
                variant="outlined"
                multiple
                chips
                closable-chips
                clearable
                :hide-details="true"
                placeholder="Appuyer sur Entrée pour ajouter un tag"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="recipe.videoUrl"
                label="Lien vidéo (YouTube, Instagram, TikTok — optionnel)"
                variant="outlined"
                :hide-details="true"
                clearable
                prepend-inner-icon="mdi-video-outline"
              />
            </v-col>
            <v-col cols="12">
              <v-row style="gap:1rem; align-items:center;">
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="recipe.imageUrl"
                    label="Image URL (optionnel)"
                    variant="outlined"
                    :hide-details="true"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center justify-end">
                  <v-sheet v-if="recipe.imageUrl" class="pa-2" elevation="0" style="max-width:140px">
                    <v-img
                      :src="previewSrc"
                      width="140"
                      height="96"
                      cover
                      @error="onPreviewError"
                    />
                  </v-sheet>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Accordéon pour ajouter un ingrédient manquant -->
      <v-expansion-panels class="mb-6" color="secondary">
        <v-expansion-panel class="bg-secondary rounded-xl">
          <v-expansion-panel-title color="secondary">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="accent">mdi-plus-circle</v-icon>
              <span class="font-weight-medium text-h6">Besoin d'un ingrédient ?</span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <IngredientSearch @ingredient-added="onIngredientAdded" />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-row>
        <!-- 1) Sélection d'ingrédients -->
        <v-col cols="12" md="7">
          <v-card color="secondary" class="rounded-xl pa-2 px-2" outlined>
            <v-card-title class="justify-space-between mb-2">
              <span class="text-h6">Ajouter des ingrédients</span>
            </v-card-title>

            <v-card-text>
              <v-text-field
                v-model="searchQuery"
                placeholder="Rechercher par nom, marque ou catégorie…"
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                density="comfortable"
                clearable
              />

              <v-skeleton-loader v-if="isLoadingIngredients" type="list-item-two-line@6" class="mt-2" />
              <div v-else>
                <v-alert v-if="!ingredients.length" type="info" variant="tonal" class="mb-2">
                  Aucun ingrédient dans votre collection pour l'instant.
                </v-alert>

                <v-alert v-else-if="!filteredIngredients.length" type="warning" variant="tonal" class="mb-2">
                  Aucun résultat pour « {{ searchQuery }} ».
                </v-alert>

                <v-virtual-scroll :items="filteredIngredients" height="360">
                  <template #default="{ item }">
                    <v-list-item
                      :title="item.productName"
                      :subtitle="subtitleFor(item)"
                      class="px-2"
                    >
                      <template #append>
                        <v-btn size="small" variant="tonal" class="mr-2" @click="openAddDialog(item)">
                          Ajouter
                        </v-btn>
                      </template>
                    </v-list-item>
                    <v-divider />
                  </template>
                </v-virtual-scroll>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- 2) Ingrédients de la recette -->
        <v-col cols="12" md="5">
          <v-card color="secondary" class="rounded-xl pa-2 px-2">
            <v-card-title class="text-h6">Ingrédients sélectionnés</v-card-title>
            <v-card-text>
              <v-alert v-if="!recipeIngredients.length" type="info" variant="tonal" class="mb-4">
                Aucun ingrédient ajouté pour l'instant.
              </v-alert>

              <div v-else class="mb-4">
                <v-row
                  v-for="(line, idx) in recipeIngredients"
                  :key="line.id"
                  align="center"
                  class="py-1"
                >
                  <v-col cols="7">
                    <div class="font-medium">{{ line.productName }}</div>
                    <div class="text-caption text-medium-emphasis">{{ line.productBrand || '—' }}</div>
                  </v-col>
                  <v-col cols="5" class="d-flex align-center justify-end">
                    <v-text-field
                      v-model.number="line.grams"
                      type="number"
                      min="0"
                      step="1"
                      suffix="g"
                      hide-details
                      density="compact"
                      style="max-width: 120px"
                      @change="clampGrams(line)"
                    />
                    <v-btn icon variant="text" color="error" @click="removeLine(idx)" class="ml-2">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-col>

                  <v-col cols="12" class="py-0">
                    <div class="text-caption">
                      ≈ {{ kcalFor(line).toFixed(0) }} kcal — Prot {{ protFor(line).toFixed(1) }} g • Gras {{ carbsFor(line).toFixed(1) }} g • Lip {{ fatFor(line).toFixed(1) }} g
                    </div>
                  </v-col>

                  <v-divider class="my-2" />
                </v-row>
              </div>

              <!-- Totaux -->
              <v-sheet class="pa-3 rounded-lg" color="tertiary">
                <div class="text-subtitle-2 mb-1">Totaux recette</div>
                <div class="text-body-2">
                  {{ totalGrams }} g — {{ totals.kcal.toFixed(0) }} kcal
                  <br>
                  P {{ totals.protein.toFixed(1) }} g • G {{ totals.carbs.toFixed(1) }} g • L {{ totals.fat.toFixed(1) }} g
                </div>
                <div v-if="recipe.servings > 0" class="text-caption mt-2">
                  <strong>Par portion :</strong>
                  {{ (totals.kcal / recipe.servings).toFixed(0) }} kcal —
                  Prot {{ (totals.protein / recipe.servings).toFixed(1) }} g •
                  Gras {{ (totals.carbs / recipe.servings).toFixed(1) }} g •
                  Lip {{ (totals.fat / recipe.servings).toFixed(1) }} g
                </div>
              </v-sheet>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Actions -->
      <div class="d-flex justify-end mt-6">
        <v-btn class="mr-2 rounded-lg" variant="outlined" @click="resetRecipe">Réinitialiser</v-btn>
        <v-btn color="accent" :disabled="!canSave" @click="saveRecipe" class="rounded-lg">
          Enregistrer la recette
        </v-btn>
      </div>

      <!-- Dialog ajout avec grammes -->
      <v-dialog v-model="addDialog.open" max-width="480">
        <v-card>
          <v-card-title>Ajouter « {{ addDialog.item?.productName }} »</v-card-title>
          <v-card-text>
            <v-text-field
              v-model.number="addDialog.grams"
              type="number"
              min="0"
              label="Quantité (g)"
              variant="outlined"
            />
            <div class="text-caption mt-1">
              Réf. 100 g : {{ nutritionLine(addDialog.item) }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn variant="text" @click="addDialog.open=false">Annuler</v-btn>
            <v-btn color="accent" @click="confirmAdd">Ajouter</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Messages -->
      <v-snackbar v-model="snack.show" :timeout="2500">
        {{ snack.text }}
      </v-snackbar>
      <v-alert v-if="errorMessage" type="error" variant="tonal" class="mt-4">
        {{ errorMessage }}
      </v-alert>
    </v-form>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { useAuthStore } from '@/store/auth_store'
  import { getUserIngredients } from "@/service/ingredient_service"
  import { addRecipeForUser, updateRecipeForUser, getUserRecipeById } from '@/service/recipe_service'
  import { getBaseIngredients } from '@/service/base_ingredient_service'
  import TopDeck from "@/components/Utils/TopDeck.vue";
  import IngredientSearch from '../Ingredients/IngredientSearch.vue'
  import RichTextEditor from '@/components/Utils/RichTextEditor.vue'


  const authStore = useAuthStore()

  /** ------- Données UI ------- **/
  const recipeCategories = [
    { text: 'Entrée', value: 'Starter' },
    { text: 'Plat principal', value: 'Meal' },
    { text: 'Dessert', value: 'Dessert' },
    { text: 'En-cas', value: 'Snacks' },
    { text: 'Petit-déjeuner', value: 'Breakfast' },
  ]

  const editId = ref(null) // id de recette si mode édition
  const isEdit = computed(() => !!editId.value)
  const route = useRoute()
  const pageTitle = computed(() => isEdit.value ? 'Modifier' : 'Créer')

  const recipe = ref({
    title: '',
    servings: 1,
    category: null,
    notes: '',
    imageUrl: '',
    labels: []
  })

  // Placeholder + preview logic for image
  const placeholderImage = 'https://assets.tmecosys.com/imagegen/production/2/7a8c1f62-8f0b-4c78-b0a0-9f3b6f4a1f2c/630x420.png'
  const previewSrc = ref(placeholderImage)

  function onPreviewError() {
    previewSrc.value = placeholderImage
  }

  // keep previewSrc in sync with recipe.imageUrl
  watch(() => recipe.value.imageUrl, (val) => {
    if (val) previewSrc.value = val
    else previewSrc.value = placeholderImage
  }, { immediate: true })

  const ingredients = ref([])                 // normalisés en tableau [{id, ...}]
  const isLoadingIngredients = ref(false)
  const errorMessage = ref('')

  const searchQuery = ref('')
  const recipeIngredients = ref([])

  const snack = ref({ show: false, text: '' })
  const isValid = ref(true)

  /** ---------- Helpers Firestore → JS ---------- **/
  function unwrapFirestoreValue(v) {
    if (v == null) return null
    if (v.stringValue !== undefined) return v.stringValue
    if (v.doubleValue !== undefined) return v.doubleValue
    if (v.integerValue !== undefined) return Number(v.integerValue)
    if (v.booleanValue !== undefined) return v.booleanValue
    if (v.arrayValue !== undefined) {
      const arr = v.arrayValue.values || []
      return arr.map(unwrapFirestoreValue)
    }
    if (v.mapValue !== undefined) {
      const out = {}
      const f = v.mapValue.fields || {}
      for (const k of Object.keys(f)) out[k] = unwrapFirestoreValue(f[k])
      return out
    }
    return v
  }

  function parseNumberFromString(s) {
    const m = String(s ?? '').match(/-?\d+(\.\d+)?/)
    return m ? Number(m[0]) : 0
  }

  function parseNutrimentsMap(nutrimentsMap) {
    // nutrimentsMap attendu: [{ key:'energy-kcal', value:'264 kcal' }, ...]
    const out = { kcal:0, protein:0, carbs:0, fat:0, sugars:0, salt:0, fiber:0, satFat:0 }
    if (!Array.isArray(nutrimentsMap)) return out
    for (const it of nutrimentsMap) {
      const key = it?.key
      const val = parseNumberFromString(it?.value)
      switch (key) {
        case 'energy-kcal': out.kcal = val; break
        case 'proteins': out.protein = val; break
        case 'carbohydrates': out.carbs = val; break
        case 'fat': out.fat = val; break
        case 'sugars': out.sugars = val; break
        case 'salt': out.salt = val; break
        case 'fiber': out.fiber = val; break
        case 'saturated-fat': out.satFat = val; break
      }
    }
    return out
  }

  function normalizeFirestoreIngredients(raw) {
    // Cas 1: le service renvoie déjà un objet simple { barcode: { ... } }
    if (raw && !raw.fields) {
      return Object.entries(raw).map(([id, val]) => {
        const nutritionPer100g =
          val.nutritionPer100g ||
          parseNutrimentsMap(val.nutriments_map) ||
          {}
        return {
          id,
          productName: val.productName || '',
          productBrand: val.productBrand || '',
          category: val.category || '',
          favorite: !!val.favorite,
          productImage: val.productImage || '',
          servingSize: val.servingSize || '',
          servingQuantity: val.servingQuantity ?? '',
          allergens: Array.isArray(val.allergens) ? val.allergens : [],
          labels: Array.isArray(val.labels) ? val.labels : [],
          novaGroup: val.novaGroup || null,
          nutriments_map: val.nutriments_map || [],
          nutritionPer100g,
        }
      })
    }

    // Cas 2: payload Firestore proto (document.fields.*)
    const fields = raw?.fields
    if (!fields) return []
    const asObject = unwrapFirestoreValue({ mapValue: { fields } }) // => { barcode: { ...js } }
    return Object.entries(asObject).map(([id, val]) => {
      const nutritionPer100g =
        parseNutrimentsMap(val.nutriments_map) || {}
      return {
        id,
        productName: val.productName || '',
        productBrand: val.productBrand || '',
        category: val.category || '',
        favorite: !!val.favorite,
        productImage: val.productImage || '',
        servingSize: val.servingSize || '',
        servingQuantity: val.servingQuantity ?? '',
        allergens: Array.isArray(val.allergens) ? val.allergens : [],
        labels: Array.isArray(val.labels) ? val.labels : [],
        novaGroup: val.novaGroup || null,
        nutriments_map: val.nutriments_map || [],
        nutritionPer100g,
      }
    })
  }

  // ------- Chargement ingrédients user + édition -------
  onMounted(() => {
    // récupère l'id si présent en query: /add_recipe?id=xxx
    editId.value = typeof route.query.id === 'string' ? route.query.id : null

    watch(
      () => authStore.userInfos,
      async () => {
        if (!authStore?.userid) return
        try {
          isLoadingIngredients.value = true
          const userData = await getUserIngredients(authStore.userid)
          const userIngredients = normalizeFirestoreIngredients(userData).map(it => ({
            ...it,
            source: 'user'
          }))

          // 2️⃣ Ingrédients de base (disponibles pour tous)
          const baseData = await getBaseIngredients()
          const baseIngredients = normalizeFirestoreIngredients(baseData).map(it => ({
            ...it,
            source: 'base'
          }))

          // 3️⃣ Fusion
          ingredients.value = [...userIngredients, ...baseIngredients]
          // après avoir les ingrédients, si on est en mode édition -> hydrate
          await tryHydrateEdit()
        } catch (e) {
          console.error('Erreur récupération ingrédients:', e)
          errorMessage.value = "Erreur lors du chargement des ingrédients."
        } finally {
          isLoadingIngredients.value = false
        }
      },
      { immediate: true }
    )

    // si l'id dans l'URL change en live (rare), on tente l'hydratation
    watch(() => route.query.id, (val) => {
      editId.value = typeof val === 'string' ? val : null
      hydratedOnce = false
      tryHydrateEdit()
    })
  })

  /** ------- Recherche ------- **/
  const filteredIngredients = computed(() => {
    const q = (searchQuery.value || '').trim().toLowerCase();
    if (!q) return ingredients.value;
    return ingredients.value.filter((item) => {
      return ['productName', 'productBrand', 'category'].some((key) => {
        return (item[key] ?? '').toLowerCase().includes(q);
      });
    });
  });

  /** ------- Callback quand un ingrédient est ajouté depuis IngredientSearch ------- **/
  async function onIngredientAdded() {
    snack.value = { show: true, text: 'Ingrédient ajouté ! Rafraîchissement de la liste...' }
    
    // Recharger les ingrédients pour inclure le nouveau
    try {
      isLoadingIngredients.value = true
      const userData = await getUserIngredients(authStore.userid)
      const userIngredients = normalizeFirestoreIngredients(userData).map(it => ({
        ...it,
        source: 'user'
      }))

      const baseData = await getBaseIngredients()
      const baseIngredients = normalizeFirestoreIngredients(baseData).map(it => ({
        ...it,
        source: 'base'
      }))

      ingredients.value = [...userIngredients, ...baseIngredients]
    } catch (e) {
      console.error('Erreur rafraîchissement ingrédients:', e)
    } finally {
      isLoadingIngredients.value = false
    }
  }

  /** ------- Ajout à la recette ------- **/
  const addDialog = ref({ open: false, item: null, grams: 100 })

  function openAddDialog(item) {
    addDialog.value = { open: true, item, grams: 100 }
  }

  function extractPer100g(item) {
    // priorité : nutritionPer100g normalisé, sinon parse nutriments_map
    if (item?.nutritionPer100g) return item.nutritionPer100g
    return parseNutrimentsMap(item?.nutriments_map || [])
  }

  // --------- Pré-remplissage en mode édition ----------
  let hydratedOnce = false

  async function tryHydrateEdit() {
    if (!isEdit.value || hydratedOnce) return
    if (!authStore.userid) return
    // on a besoin que la collection d'ingrédients soit dispo pour hydrater joliment
    if (!ingredients.value.length) return

    try {
      // 🔹 Récupère UNIQUEMENT cette recette
      const r = await getUserRecipeById(authStore.userid, editId.value)
      if (!r) {
        errorMessage.value = 'Recette introuvable.'
        return
      }

      // Pré-remplir meta
      recipe.value = {
        title: r.title || '',
        servings: Number(r.servings || 1),
        category: r.category ?? null,
        notes: r.notes || '',
        imageUrl: r.imageUrl || '',
        videoUrl: r.videoUrl || ''
      }

      // Hydrater lignes ingréds [{id, grams}] -> [{id, grams, productName, brand, nutritionPer100g}]
      const lines = Array.isArray(r.ingredients) ? r.ingredients : []
      recipeIngredients.value = lines.map((l) => {
        const found = ingredients.value.find(it => it.id === l.id)
        return {
          id: l.id,
          grams: Number(l.grams || 0),
          novaGroup: l.novaGroup ?? found?.novaGroup ?? null,
          productName: found?.productName || l.id,
          productBrand: found?.productBrand || '',
          nutritionPer100g: extractPer100g(found) // {} si pas trouvé
        }
      })

      hydratedOnce = true
    } catch (e) {
      console.error('❌ Hydratation édition échouée:', e)
      errorMessage.value = "Impossible de pré-remplir la recette."
    }
  }

  function confirmAdd() {
    const { item, grams } = addDialog.value
    if (!item || grams <= 0) return
    const existing = recipeIngredients.value.find(l => l.id === item.id)
    if (existing) {
      existing.grams += Number(grams)
    } else {
      recipeIngredients.value.push({
        id: item.id,
        name: item.productName,
        productName: item.productName,
        productBrand: item.productBrand,
        novaGroup: item.novaGroup ?? null,
        grams: Number(grams),
        nutritionPer100g: extractPer100g(item)
      })
    }
    addDialog.value.open = false
  }

  function removeLine(index) { recipeIngredients.value.splice(index, 1) }
  function clampGrams(line) {
    if (line.grams == null || isNaN(line.grams) || line.grams < 0) line.grams = 0
  }

  /** ------- Nutrition & totaux ------- **/
  function factor(line) { return (line.grams || 0) / 100 }
  function kcalFor(line)  { return (line.nutritionPer100g.kcal   || 0) * factor(line) }
  function protFor(line)  { return (line.nutritionPer100g.protein|| 0) * factor(line) }
  function carbsFor(line) { return (line.nutritionPer100g.carbs  || 0) * factor(line) }
  function fatFor(line)   { return (line.nutritionPer100g.fat    || 0) * factor(line) }

  const totals = computed(() => {
    return recipeIngredients.value.reduce((acc, l) => {
      const f = factor(l)
      acc.kcal   += (l.nutritionPer100g.kcal    || 0) * f
      acc.protein+= (l.nutritionPer100g.protein || 0) * f
      acc.carbs  += (l.nutritionPer100g.carbs   || 0) * f
      acc.fat    += (l.nutritionPer100g.fat     || 0) * f
      acc.sugars += (l.nutritionPer100g.sugars  || 0) * f
      acc.salt   += (l.nutritionPer100g.salt    || 0) * f
      return acc
    }, { kcal:0, protein:0, carbs:0, fat:0, sugars:0, salt:0 })
  })

  const totalGrams = computed(() =>
    recipeIngredients.value.reduce((s, l) => s + (l.grams || 0), 0)
  )

  /** ------- Helpers UI ------- **/
  function subtitleFor(item) {
    const left = [item.productBrand, item.category].filter(Boolean).join(' • ')
    const n = extractPer100g(item)
    const right = `${n.kcal || 0} kcal / 100 g`
    return left ? `${left}  —  ${right}` : right
  }
  function nutritionLine(item) {
    const n = extractPer100g(item)
    return `${n.kcal || 0} kcal • P ${n.protein || 0} g • G ${n.carbs || 0} g • L ${n.fat || 0} g / 100 g`
  }

  /** ------- Validation & Enregistrement ------- **/
  const canSave = computed(() => {
    return !!recipe.value.title && recipeIngredients.value.length > 0 && isValid.value
  })

  async function saveRecipe() {
    if (!authStore.userid) {
      errorMessage.value = "Utilisateur non connecté."
      return
    }
    const payload = {
      date: Date.now(),
      imageUrl: recipe.value.imageUrl || '',
      videoUrl: recipe.value.videoUrl || '',
      title: recipe.value.title,
      category: recipe.value.category || null,
      servings: Number(recipe.value.servings || 1),
      notes: formatNotesAsHtml(recipe.value.notes),
      ingredients: recipeIngredients.value.map(l => ({
        id: l.id,
        name: l.name || l.productName,
        grams: l.grams,
        novaGroup: l.novaGroup ?? null,
      })),
      totals: {
        grams: totalGrams.value,
        kcal: Number(totals.value.kcal.toFixed(0)),
        protein: Number(totals.value.protein.toFixed(1)),
        carbs: Number(totals.value.carbs.toFixed(1)),
        fat: Number(totals.value.fat.toFixed(1)),
        sugars: Number(totals.value.sugars.toFixed(1)),
        salt: Number(totals.value.salt.toFixed(2))
      }
    }

    try {
      if (isEdit.value) {
        // UPDATE
        await updateRecipeForUser(authStore.userid, editId.value, {
          ...payload,
          // si tu veux conserver une date de création existante, ne remets pas createdAt
          updatedAt: Date.now(),
        })
        snack.value = { show: true, text: 'Recette mise à jour ✅' }
      } else {
        // CREATE
        await addRecipeForUser(authStore.userid, {
          ...payload,
          createdAt: Date.now(),
        })
        snack.value = { show: true, text: 'Recette enregistrée ✅' }
        resetRecipe()
      }
    } catch (e) {
      console.error('Erreur enregistrement recette:', e)
      errorMessage.value = isEdit.value
        ? "Erreur lors de la mise à jour de la recette."
        : "Erreur lors de l'enregistrement de la recette."
    }
  }

  function resetRecipe() {
    recipe.value = { title: '', servings: 1, category: null, notes: '', imageUrl: '', videoUrl: '' }
    searchQuery.value = ''
    recipeIngredients.value = []
  }

  function formatNotesAsHtml(text) {
    if (!text) return ''
    return String(text)
      .split('\n')
      .map(line => {
        const trimmed = line.trim()
        if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
          return '<li>' + trimmed.substring(1).trim() + '</li>'
        }
        return trimmed ? '<p>' + trimmed + '</p>' : '<br/>'
      })
      .join('')
      .replace(/(<li>.*?<\/li>)/s, (match) => '<ul>' + match + '</ul>')
  }
</script>
